import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

import { useAuth } from "./AuthContext";
import type { AppDispatch, RootState } from "../../store";
import { sendAxiosRequestWithRetry } from '../../services/sendAxiosRequestWithRetry';
import { showCustomErrorToast, showErrorToast, showServerErrorToast } from '../../helpers/toasts';
import { normalizeDialCodeSymbol, normalizePhoneLocalNumber } from '../../helpers/phone';
import { getCurrentServiceOpeningStatus } from '../../helpers/openingHours';
import { canEditOrderItemByPermission, isDetailedOrderData } from '../../helpers/uiConditions';
import { getOrderCheckout, isExpiredPendingPaymentCheckout } from '../../helpers/pendingPayments';
import { useRestaurantDetails } from './RestaurantDetailsContext';
import { getApiErrorText } from "../../shared/api/errors";

import { getServingTypeTitle } from "../../entities/serving-type/model/servingTypes";
import { changeDemoModeState, setShowDemoModeBtnLoader, toggleSwapToOrderAddedItemsInMobileView } from '../../store/slices/appGeneralSlice';
import {
    selectStep,
    setCreatingNewOrderLoading,
    addToTableOrders,
    updateTableOrder,
    setOrderAddedItemsLoading,
    activateLoaderForAvailableItemsList,
    activateLoaderForOrdersList,
    setTableOrdersListData,
    activateLoaderForTablesList,
    setSelectedCategoryIdOrType,
    activateLoaderForOlderOrdersList,
    changeCurrentOrderData,
    setSelectedTableOrOrderTypeInfo,
    setSelectedOrder,
    setEdibleItemPreviewDetails,
    setAvalableItems,
    setTableAndOrderTypesListData,
    changeCurrentEditingData,
    setItemPreviewBoxLoading,
    setEdibleCategoriesLoading,
    setEdibleCategories,
    setOrdersListBackgroundLoading,
} from '../../store/slices/stepManagementSlice';
import { AddCustomEdibleItemData, AddManualEdibleItemData, AddNormalEdibleItemData, UpdateCustomEdibleItemData, UpdateManualEdibleItemData, UpdateNormalEdibleItemData } from "../../shared/types/edibles";
import { getNormalizedOrderType } from "../../shared/lib/order";

type OrderListFilterParams = Partial<CashBoxOrdersListFilters>;

type TableSelectionParams = {
    tableId?: number | null;
    orderType?: ApiOrderType | null;
};

type OrdersListParams = TableSelectionParams & {
    selectedOrderId?: number | null;
    noNeedToUpdate?: boolean;
    silentFetching?: boolean;
    onlyUpdateHistoryItems?: boolean;
};

type OpenOrderByDetailsParams = {
    orderDetails?: CashBoxOrderSummary | CashBoxOrderDetails | null;
};

type SelectOrderParams = {
    orderId?: number | null;
    silentSelecting?: boolean;
    doNotUpdateOrderId?: boolean;
};

type MutationResult<T> = {
    // respData?: Partial<T>;
    respData?: T;
    respError?: ApiError;
};

type MutationCallbacks<T> = {
    afterSuccess?: (data?: Partial<T>) => void;
    afterFailure?: (error?: ApiError) => void;
    afterComplete?: (result: MutationResult<T>) => void;
};

type AddEdibleItemParams = {
    orderId?: number | null;
    itemType?: EdibleGeneralType;
    itemData?: AddNormalEdibleItemData | AddCustomEdibleItemData | AddManualEdibleItemData;
    dontShowLoadingForAllItems?: boolean;
    afterComplete?: (result: MutationResult<CashBoxOrderDetails>) => void;
};

export type UpdateEdibleItemParams = {
    orderId?: number | null;
    itemType?: EdibleGeneralType;
    itemData: UpdateNormalEdibleItemData | UpdateCustomEdibleItemData | UpdateManualEdibleItemData;
    dontShowLoadingForAllItems?: boolean;
    afterComplete?: (result: MutationResult<CashBoxOrderDetails>) => void;
};

type ChangeTableParams = {
    tableId: number;
    orderId: number;
};

type AcceptDeliveryOrPickupOrderParams = MutationCallbacks<CashBoxOrderDetails> & {
    orderId?: number | null;
    pickupDateTime?: string;
    isPickup?: boolean;
    email?: string;
    countrySymbol?: string;
    phoneNumber?: string;
    deliveryData?: {
        receiverLatitude?: number | null;
        receiverLongitude?: number | null;
        fullAddress?: string | null;
        country?: string | null;
        state?: string | null;
        city?: string | null;
        locality?: string | null;
        street?: string | null;
        houseNumber?: string | null;
        postalCode?: string | null;
        doorNumber?: string | null;
        apartmentEntrance?: string | null;
        floor?: string | null;
        extraDetails?: string | null;
        description?: string | null;
        receiverFullName?: string | null;
    };
};

type AcceptDeliveryOrPickupOrderRequestData = {
    order_id: number;
    pickup_time?: string;
    email?: string;
    phone_number?: CashBoxReceiverPhone;
    delivery?: {
        latitude: number | null;
        longitude: number | null;
        full_address: string;
        country: string | null;
        state: string | null;
        city: string | null;
        locality: string | null;
        street: string | null;
        house_number: string | null;
        postal: string | null;
        door_number: string | null;
        apartment_entrance: string | null;
        floor: string | null;
        extra_details: string | null;
        description: string | null;
        full_name: string | null;
        email: string | null;
        phone_number: CashBoxReceiverPhone | null;
    };
};

type EditingItemParams = {
    id?: CashBoxId;
    itemId?: CashBoxId;
    itemType: Exclude<EdibleGeneralType, "manual">;
    currentItemData?: CashBoxOrderItem | CashBoxCustomOrderItem | null;
};

type ItemPreviewParams = {
    itemId?: CashBoxId;
    itemType: EdibleGeneralType;
};

type SendToKitchenParams = {
    orderId?: number | null;
    description?: string;
    selectedItems?: Array<{ id?: CashBoxId }>;
    selectedCustomItems?: Array<{ id?: CashBoxId }>;
    selectedManualItems?: Array<{ id?: CashBoxId }>;
};

type ToggleDemoModeParams = {
    demoMode?: boolean;
    targetTableId?: number | null;
    targetOrderType?: ApiOrderType | null;
    targetOrderId?: number | null;
    afterSuccess?: (isDemoMode?: boolean) => void;
    afterError?: (error?: ApiError) => void;
};

type ToggleOrderDemoParams = {
    orderId?: number | null;
    afterSuccess?: (data?: CashBoxOrderDetails) => void;
    afterError?: (error?: ApiError) => void;
};

interface OrderFlowContextValue {
    showOlderOrders?: boolean;
    showDeliveredOrders: boolean;
    showPaidOrders: boolean;
    changeFiltersAppliedToOrdersList: (params: OrderListFilterParams) => void;
    goToTargetStepFromSearchParams: () => void;
    updateTablesList: (showErrorMessageToast?: boolean | undefined, showLoading?: boolean) => Promise<ApiResult<CashBoxTableOrOrderTypeItem[]>>;
    goToTablesListStep: () => void;
    goToTablesListStepAndUpdateData: () => Promise<void> | void;
    selectTable: (params: TableSelectionParams) => void;
    updateSelectedTableOrders: (params: TableSelectionParams) => void;
    goToOrdersListStep: (params: OrdersListParams) => Promise<void>;
    updateOrdersList: (params: OrdersListParams) => Promise<void>;
    createOrder: (params: TableSelectionParams) => Promise<void>;
    openOrderByOrderDetails: (params: OpenOrderByDetailsParams) => Promise<void>;
    refetchOrderDetailsAndUpdate: (params: SelectOrderParams) => void;
    selectOrder: (params: SelectOrderParams) => Promise<void>;
    unselectOrder: () => void;
    cancelOrder: (params: {
        orderId?: number | null;
    } & MutationCallbacks<CashBoxOrderDetails>) => Promise<MutationResult<CashBoxOrderDetails> | void>;
    cancelOrderWithConfirm: (params: {
        orderId?: number | null;
        afterConfirm?: () => void;
    } & MutationCallbacks<CashBoxOrderDetails>) => void;
    acceptDeliveryOrPickupOrder: (params: AcceptDeliveryOrPickupOrderParams) => Promise<MutationResult<CashBoxOrderDetails> | void>;
    goToAvailableItemsStep: (params: {
        catIdOrType?: number | string | null;
        orderType?: ApiOrderType | null;
        noNeedToUpdate?: boolean;
        categoriesSilentFetch?: boolean;
        doNotUpdateCategories?: boolean;
    }) => Promise<void>;
    addItemToOrder: (params: AddEdibleItemParams) => Promise<void>;
    updateItemOfOrder: (params: UpdateEdibleItemParams) => Promise<"required_data_unavailable" | "succeeded" | "failed">;
    getPreviewDataOfSelectedCustomItemOptions: (params: {
        customizationData: AddCustomEdibleItemData | UpdateCustomEdibleItemData
    }) => Promise<ApiResult<CashBoxCustomOrderPreview>>;
    deleteItemOfOrder: (params: {
        orderId?: number | null;
        id?: CashBoxId | null;
        itemType?: string | boolean;
    }) => void;
    provideEditingItemModalData: (params: EditingItemParams) => Promise<void>;
    clearEditingItemModalData: () => void;
    goToItemPreview: (params: ItemPreviewParams) => Promise<void>;
    sendSelectedItemsTokitchen: (params: SendToKitchenParams) => Promise<void>;
    deliverOrder: (params: { orderId?: number | null }) => void;
    changeOrderTable: (params: ChangeTableParams) => Promise<ApiResult<CashBoxOrderDetails>>;
    toggleDemoMode: (params: ToggleDemoModeParams) => Promise<void>;
    toggleOrderDemo: (params: ToggleOrderDemoParams) => void;
}

const getTranslatedText = (value: unknown) => `${value ?? ""}`;


const ConfirmDialog = withReactContent(Swal);


const OrderFlowContext = createContext<OrderFlowContextValue | null>(null);

export const OrderFlowProvider = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { restaurantDetails } = useRestaurantDetails();

    const { isAuthenticated } = useAuth();

    const hasInitializedRef = useRef(false);

    const appIsDemoMode = useSelector((state: RootState) => state.appGeneral.isDemoMode);
    const appIsDemoModeRef = useRef<boolean>(undefined);

    const isMobileView = useSelector((state: RootState) => state.appGeneral.isMobileView);
    const isMobileViewRef = useRef<boolean | undefined>(undefined);

    const availableEdiblesList = useSelector((state: RootState) => state.stepManagement.availableItemsList);
    const availableEdiblesListRef = useRef<CashBoxNormalFoodData[] | CashBoxCustomFoodData[] | null>(undefined);

    const tablesList = useSelector((state: RootState) => state.stepManagement.tablesList);
    const tablesListRef = useRef<CashBoxTableOrOrderTypeItem[] | undefined>(undefined);

    const currentTableId = useSelector((state: RootState) => state.stepManagement.currentTableId);
    const currentTableIdRef = useRef<number | null | undefined>(undefined);

    const currentOrderType = useSelector((state: RootState) => state.stepManagement.currentOrderType);
    const currentOrderTypeRef = useRef<ApiOrderType | null | undefined>(undefined);

    const currentOrderId = useSelector((state: RootState) => state.stepManagement.currentOrderId);
    const currentOrderIdRef = useRef<number | null | undefined>(undefined);

    const currentOrderDetails = useSelector((state: RootState) => state.stepManagement.currentOrderDetails);
    const currentOrderDetailsRef = useRef<RootState["stepManagement"]["currentOrderDetails"]>(null);

    const currentTableOrdersList = useSelector((state: RootState) => state.stepManagement.currentTableOrdersList);
    const currentTableOrdersListRef = useRef<(CashBoxOrderSummary | CashBoxOrderDetails)[] | null>(null);

    const outOfPendingStatusCheckouts = useSelector((state: RootState) => state.paymentManagement.outOfPendingStatusCheckouts);
    const outOfPendingStatusCheckoutsRef = useRef<RootState["paymentManagement"]["outOfPendingStatusCheckouts"]>({});


    const [showOlderOrders, setShowOlderOrders] = useState<boolean | undefined>();
    const showOlderOrdersRef = useRef<boolean | undefined>(undefined);

    const [showDeliveredOrders, setShowDeliveredOrders] = useState(true);
    const showDeliveredOrdersRef = useRef(true);

    const [showPaidOrders, setShowPaidOrders] = useState(true);
    const showPaidOrdersRef = useRef<boolean | undefined>(undefined);
    const canUseDemoModePermission = restaurantDetails?.waiter_permissions?.demo_mode !== false;
    const canCancelOrderPermission = restaurantDetails?.waiter_permissions?.cancel_order !== false;


    useEffect(() => {
        const isBeforeLoginRoute = !isAuthenticated || window.location.pathname.startsWith("/login");
        if (isBeforeLoginRoute) return;

        if (hasInitializedRef.current) return;

        goToTargetStepFromSearchParams();

        hasInitializedRef.current = true;
    }, [isAuthenticated]);

    useEffect(() => {
        if (
            currentTableId !== undefined ||
            currentOrderType !== undefined ||
            currentOrderId !== undefined
        ) {
            syncUrl({
                tableId: currentTableId,
                orderType: currentOrderType,
                orderId: currentOrderId
            });
        }
    }, [currentTableId, currentOrderType, currentOrderId]);

    useEffect(() => {
        appIsDemoModeRef.current = appIsDemoMode;
    }, [appIsDemoMode]);

    useEffect(() => {
        isMobileViewRef.current = !!isMobileView;
    }, [isMobileView]);

    useEffect(() => {
        availableEdiblesListRef.current = availableEdiblesList;
    }, [availableEdiblesList]);

    useEffect(() => {
        if (Array.isArray(tablesList)) {
            tablesListRef.current = tablesList;
        }
    }, [tablesList]);

    useEffect(() => {
        currentTableIdRef.current = currentTableId;
    }, [currentTableId]);

    useEffect(() => {
        currentOrderTypeRef.current = currentOrderType;
    }, [currentOrderType]);

    useEffect(() => {
        currentOrderIdRef.current = currentOrderId;
    }, [currentOrderId]);

    useEffect(() => {
        currentOrderDetailsRef.current = currentOrderDetails;
    }, [currentOrderDetails]);

    useEffect(() => {
        if (Array.isArray(currentTableOrdersList)) {
            currentTableOrdersListRef.current = currentTableOrdersList;
        }
    }, [currentTableOrdersList]);

    useEffect(() => {
        outOfPendingStatusCheckoutsRef.current = outOfPendingStatusCheckouts || {};
    }, [outOfPendingStatusCheckouts]);


    const syncUrl = (params: Record<string, unknown>) => {
        const currSP = new URLSearchParams(window.location.search);

        const searchParamsObj = {
            ...Object.fromEntries(currSP),
            ...params,
        }

        let changed = false;
        Object.entries(searchParamsObj).forEach(([key, val]) => {
            if ((currSP.get(key) ?? null) !== (val?.toString() ?? null)) {
                changed = true;
            }
        });
        if (!changed) return;

        const nextSearchParams = Object.fromEntries(
            Object.entries(searchParamsObj)
                .filter(([key, val]) => key && val)
                .map(([key, val]) => [key, `${val}`])
        );

        setSearchParams(nextSearchParams);
    };


    /* ---------------------------
    * Init from URL (once)
    * --------------------------- */
    const goToTargetStepFromSearchParams = () => {
        const orderId = Number(searchParams.get('orderId'));
        const tableId = Number(searchParams.get('tableId'));
        const orderType = getNormalizedOrderType(searchParams.get('orderType') || "");

        if (orderId) {
            _openOrderByOrderId({
                orderId,
                tableId,
                orderType,
            });
        }
        else if (tableId || orderType) {
            dispatch(setSelectedTableOrOrderTypeInfo({
                orderType: orderType !== "normal" ? orderType : null,
                tableId,
            }));

            updateOrdersTableTitleAndTablesList({
                tableId,
                orderType,
            });

            selectTable({
                tableId,
                orderType,
            });
        }
        else {
            goToTablesListStepAndUpdateData();
        }
    };


    /* ---------------------------
    * Filters (Of orders list)
    * --------------------------- */
    const changeFiltersAppliedToOrdersList = ({
        showOlderOrders: showOlders,
        showDeliveredOrders: showDelivered,
        showPaidOrders: showPaids,
    }: OrderListFilterParams) => {
        if (showOlders !== undefined) {
            setShowOlderOrders(showOlders);
            showOlderOrdersRef.current = !!showOlders;
        }
        if (showDelivered !== undefined) {
            setShowDeliveredOrders(showDelivered);
            showDeliveredOrdersRef.current = !!showDelivered;
        }
        if (showPaids !== undefined) {
            setShowPaidOrders(showPaids);
            showPaidOrdersRef.current = !!showPaids;
        }
    };


    /* ---------------------------
    * Table
    * --------------------------- */
    const updateTablesList = async (showErrorMessageToast?: boolean, showLoading?: boolean) => {
        if (showLoading) {
            dispatch(activateLoaderForTablesList());
        }

        const resp = await sendAxiosRequestWithRetry<CashBoxTableOrOrderTypeItem[]>({
            method: "get",
            url: "/table/list"
        });

        const {
            data: respData,
            error: respError
        } = resp;

        if (respData) {
            dispatch(setTableAndOrderTypesListData({
                tablesList: respData
            }));
        }
        else if (respError) {
            if (showErrorMessageToast) {
                const errorText = getApiErrorText(respError);

                showErrorToast({
                    message: errorText,
                    status: respError.statusCode,
                });
            }
        }

        return resp;
    };

    const goToTablesListStep = () => {
        dispatch(selectStep({
            targetStepId: 'tablesListShowStep',
        }));

        dispatch(changeCurrentOrderData({
            currentOrderDetails: null,
        }));

        dispatch(setSelectedTableOrOrderTypeInfo({
            tableId: null,
            orderType: null,
            tableTitle: null,
        }));

        dispatch(setSelectedCategoryIdOrType({
            selectedCategoryIdOrType: undefined
        }));
    };

    const goToTablesListStepAndUpdateData = async () => {
        goToTablesListStep();
        await updateTablesList(false, true);
    };

    const updateOrdersTableTitleAndTablesList = async ({
        tableId,
        orderType,
    }: TableSelectionParams) => {
        const {
            data: respData,
            // error: respError
        } = await updateTablesList();

        if (respData) {
            setTimeout(() => {
                dispatch(setSelectedTableOrOrderTypeInfo({
                    tableId,
                    orderType,
                    tableTitle: getServingTypeTitle({
                        tableId,
                        orderType,
                        tablesListData: tablesListRef.current,
                        t
                    }),
                }));
            }, 0);
        }
    };

    const selectTable = ({
        tableId,
        orderType,
    }: TableSelectionParams) => {
        if (!tableId && !orderType) return;

        goToOrdersListStep({
            tableId,
            orderType,
        });
    };

    const updateSelectedTableOrders = ({
        tableId,
        orderType
    }: TableSelectionParams) => {
        if (!tableId && !orderType) return;

        goToOrdersListStep({
            tableId,
            orderType,
        });
    };


    /* ---------------------------
    * Order
    * --------------------------- */
    const getOrdersListWithUpdatedCurrentOrder = (targetOrderDetails: CashBoxOrderSummary | CashBoxOrderDetails) => {
        let ordersList_clone: (CashBoxOrderSummary | CashBoxOrderDetails)[] = currentTableOrdersListRef.current
            ? [...currentTableOrdersListRef.current]
            : [];

        ordersList_clone = ordersList_clone.map(order => (
            order.id === currentOrderIdRef.current ? targetOrderDetails : order
        ));

        return ordersList_clone;
    };

    const goToOrdersListStep = async ({
        tableId,
        orderType,
        selectedOrderId,
        noNeedToUpdate
    }: OrdersListParams) => {
        dispatch(selectStep({
            targetStepId: 'ordersListShowStep',
        }));

        if (noNeedToUpdate) return;

        // dispatch(setTableOrdersListData({
        //     tableOrdersList: null,
        //     tableOlderOrdersList: null,
        // }));

        if (
            (tableId && tableId !== currentTableIdRef.current) ||
            (orderType && orderType !== currentOrderTypeRef.current)
        ) {
            dispatch(setSelectedTableOrOrderTypeInfo({
                orderType: orderType !== "normal" ? orderType : null,
                tableId,
            }));
        }

        if (selectedOrderId) {
            dispatch(setSelectedOrder({ orderId: selectedOrderId }));

            dispatch(setOrderAddedItemsLoading(true));
        }

        await updateOrdersList({
            tableId,
            orderType,
        });

        dispatch(setOrderAddedItemsLoading(false));
    };

    const updateOrdersList = async ({
        tableId,
        orderType,
        silentFetching,
        onlyUpdateHistoryItems,
    }: OrdersListParams) => {
        // dispatch(setTableOrdersListData({
        //     tableOrdersList: null,
        //     tableOlderOrdersList: null,
        // }));

        dispatch(setOrdersListBackgroundLoading(true));

        if (!silentFetching) {
            if (!onlyUpdateHistoryItems) {
                dispatch(activateLoaderForOrdersList());
            }

            if (showOlderOrdersRef.current) {
                dispatch(activateLoaderForOlderOrdersList());
            }
        }

        let ordersResp;
        if (!onlyUpdateHistoryItems) {
            ordersResp = await sendAxiosRequestWithRetry<CashBoxOrderSummary[]>({
                method: "get",
                url: "/order/list",
                params: {
                    table_id: tableId || orderType,
                },
            });
        }
        const {
            data: ordersList_fetchData,
            error: ordersList_fetchError
        } = ordersResp || {};

        let olderOrdersResp;
        if (showOlderOrdersRef.current) {
            olderOrdersResp = await sendAxiosRequestWithRetry<CashBoxOrderSummary[]>({
                method: "get",
                url: "/order/list",
                params: {
                    table_id: tableId || orderType,
                    history_filter: true
                },
            });
        }

        const {
            data: olderOrdersList_fetchData,
            error: olderOrdersList_fetchError
        } = olderOrdersResp || {};

        if (ordersList_fetchData || olderOrdersList_fetchData) {
            if (olderOrdersList_fetchError) {
                //
            }

            dispatch(setTableOrdersListData({
                ...(ordersList_fetchData && { tableOrdersList: ordersList_fetchData }),
                ...(olderOrdersList_fetchData && { tableOlderOrdersList: olderOrdersList_fetchData }),
                // ...(olderOrdersList_fetchError && { tableOlderOrdersList: null }),
            }));
        }
        else if (ordersList_fetchError) {
            //
        }

        dispatch(setOrdersListBackgroundLoading(false));
    };

    const createOrder = async ({
        tableId,
        orderType
    }: TableSelectionParams) => {
        if ((!tableId && orderType === "normal") || !orderType) return;

        if (orderType === "normal") {
            const normalOrderOpeningStatus = getCurrentServiceOpeningStatus({
                openingHours: restaurantDetails?.opening_hours,
                serviceKind: "normal",
            });
            const closedMessage = String(
                normalOrderOpeningStatus.isClosedAllDay
                    ? t("normalOrderCreationClosedAllDay")
                    : t("normalOrderCreationClosedAtThisTime", {
                        hours: normalOrderOpeningStatus.timeRangesText || "- - -",
                    })
            );

            if (!normalOrderOpeningStatus.isOpenNow) {
                showErrorToast({
                    message: closedMessage,
                    status: undefined,
                });
                return;
            }
        }

        dispatch(setCreatingNewOrderLoading(true));

        const {
            data: respData,
            error: respError
        } = await sendAxiosRequestWithRetry<CashBoxOrderDetails>({
            method: "post",
            url: "/order/create_order",
            params: {
                table_id: tableId,
                order_type: orderType
            },
        });

        if (respData) {
            dispatch(addToTableOrders({
                orderDetails: respData,
                isSelectedOrder: true,
            }));

            goToAvailableItemsStep({
                orderType
            });

        }
        else if (respError) {
            const errorText = getApiErrorText(respError);

            showErrorToast({
                message: errorText,
                status: respError.statusCode,
            });
        };

        dispatch(setCreatingNewOrderLoading(false));
    };

    const openOrderByOrderDetails = async ({
        orderDetails,
    }: OpenOrderByDetailsParams) => {
        if (!orderDetails) return;

        const {
            id: orderId,
            table_id: tableId,
            order_type: orderType,
        } = orderDetails || {};

        const tableNumber = "table_number" in orderDetails
            ? orderDetails.table_number
            : null;

        dispatch(selectStep({
            targetStepId: 'ordersListShowStep',
        }));

        dispatch(updateTableOrder({
            orderDetails,
            isSelectedOrder: true
        }));

        dispatch(toggleSwapToOrderAddedItemsInMobileView({
            swapToOrderAddedItemsBox: true
        }));

        dispatch(setSelectedTableOrOrderTypeInfo({
            orderType: orderType !== "normal" ? orderType : null,
            tableId,
            tableTitle: getServingTypeTitle({
                tableId,
                orderType,
                tableNumber,
                tablesListData: tablesListRef.current,
                t
            }),
        }));

        await updateOrdersList({
            tableId,
            orderType,
            silentFetching:
                Boolean(
                    (currentOrderTypeRef.current && currentOrderTypeRef.current === orderType) ||
                    (currentTableIdRef.current && currentTableIdRef.current === tableId)
                ),
        });

        // dispatch(setSelectedOrder({ orderId: orderDetails.id }));

        // Call 'selectOrder' to get full details of order (for example to get added items 'image' or ...)
        selectOrder({
            orderId,
            // silentSelecting: currentOrderIdRef.current && currentOrderIdRef.current === orderId,
            silentSelecting: true,
        });
    };

    const _openOrderByOrderId = async ({
        orderId,
        tableId,
        orderType,
    }: SelectOrderParams & TableSelectionParams) => {
        if (!orderId) return;

        if (
            (tableId && tableId !== currentTableIdRef.current) ||
            (orderType && orderType !== currentOrderTypeRef.current)
        ) {
            dispatch(setSelectedTableOrOrderTypeInfo({
                orderType: orderType !== "normal" ? orderType : null,
                tableId,
                tableTitle: getServingTypeTitle({
                    tableId,
                    orderType,
                    tablesListData: tablesListRef.current,
                    t
                }),
            }));
        }

        dispatch(setSelectedOrder({ orderId }));

        await updateOrdersList({
            tableId,
            orderType,
        });

        selectOrder({ orderId });
    };

    const selectOrder = async ({
        orderId,
        silentSelecting,
        doNotUpdateOrderId,    // Prevent blinking the data
    }: SelectOrderParams) => {
        if (!orderId) return;

        if (!silentSelecting) {
            dispatch(selectStep({
                targetStepId: 'ordersListShowStep',
            }));

            dispatch(toggleSwapToOrderAddedItemsInMobileView({
                swapToOrderAddedItemsBox: true
            }));

            dispatch(setOrderAddedItemsLoading(true));
        }

        if (!doNotUpdateOrderId) {
            dispatch(setSelectedOrder({ orderId }));
        }

        const {
            data: respData,
            error: respError
        } = await sendAxiosRequestWithRetry<CashBoxOrderDetails>({
            method: "get",
            url: "/order/details",
            params: {
                order_id: orderId
            },
        });

        if (respData) {
            const {
                order_type: orderType,
                table_id: tableId,
                table_number: tableNumber,
            } = respData;

            dispatch(updateTableOrder({
                orderDetails: respData,
                isSelectedOrder: true
            }));

            dispatch(setSelectedTableOrOrderTypeInfo({
                orderType: orderType !== "normal" ? orderType : null,
                tableId,
                tableTitle: getServingTypeTitle({
                    tableId,
                    orderType,
                    tableNumber,
                    tablesListData: tablesListRef.current,
                    t
                }),
            }));
        }
        else if (respError) {
            dispatch(changeCurrentOrderData({
                currentOrderDetails: null,
                errorText:
                    getApiErrorText(respError),
            }));
        }

        dispatch(setOrderAddedItemsLoading(false));
    };

    const refetchOrderDetailsAndUpdate = ({
        orderId,
        silentSelecting,
    }: Pick<SelectOrderParams, "orderId" | "silentSelecting">) => {
        selectOrder({
            orderId,
            silentSelecting,
            doNotUpdateOrderId: true,
        });
    };

    const canEditTargetOrderItem = ({
        orderId,
        itemId,
        itemType = "normal",
    }: {
        orderId?: number | null;
        itemId?: CashBoxId | null;
        itemType?: EdibleGeneralType;
    }) => {
        const targetOrderId = orderId || currentOrderIdRef.current;
        const orderDetails = currentOrderDetailsRef.current;

        if (!itemId || !targetOrderId || !orderDetails || orderDetails.id !== targetOrderId) {
            return true;
        }

        if (!isDetailedOrderData(orderDetails)) {
            return true; // Let backend decide about it
        }

        const targetItem =
            itemType === "custom"
                ? orderDetails.customItems.find(item => item.id === itemId) :
            itemType === "manual"
                ? orderDetails.freeItems.find(item => item.id === itemId)
                : orderDetails.items.find(item => item.id === itemId);

        const targetCheckoutId = targetItem?.checkout_id;
        const shouldTreatItemAsUnpaid =
            !!targetItem &&
            !targetItem.is_paid &&
            (
                !targetCheckoutId ||
                (
                    !outOfPendingStatusCheckoutsRef.current?.[targetCheckoutId] &&
                    isExpiredPendingPaymentCheckout(
                        getOrderCheckout(orderDetails, targetCheckoutId),
                        Date.now()
                    )
                )
            );

        const editableTargetItem = shouldTreatItemAsUnpaid
            ? {
                ...targetItem,
                checkout_id: null,
            }
            : targetItem;

        return canEditOrderItemByPermission(
            editableTargetItem,
            restaurantDetails?.waiter_permissions
        );
    };

    const unselectOrder = () => {
        dispatch(setSelectedOrder({
            orderId: null,
        }));

        dispatch(setOrderAddedItemsLoading(false));
    };

    const cancelOrder = async ({
        orderId,
        afterSuccess,
        afterFailure,
    }: { orderId?: number | null } & MutationCallbacks<CashBoxOrderDetails>) => {
        if (!orderId || !canCancelOrderPermission) return;

        const {
            data: respData,
            error: respError,
        } = await sendAxiosRequestWithRetry<CashBoxOrderDetails>({
            method: "post",
            url: "/order/reject",
            data: {
                order_id: orderId,
            },
        });

        if (respData) {
            if (currentOrderIdRef.current === orderId) {
                dispatch(setSelectedOrder({
                    orderId: null
                }));
            }

            if (
                (currentTableIdRef.current && currentTableIdRef.current === respData.table_id) ||
                (currentOrderTypeRef.current && currentOrderTypeRef.current === respData.order_type)
            ) {
                updateSelectedTableOrders({
                    tableId: respData.table_id,
                    orderType: respData.order_type,
                });
            }

            afterSuccess?.(respData);
        }
        else if (respError) {
            showServerErrorToast({
                err_status: respError.statusCode,
                err_message: respError?.message || "",
                list_errors: respError.details?.errors,
                opts: {
                    errorTitle: t('Error'),
                },
            });

            afterFailure?.(respError);
        }

        return {
            respData,
            respError
        };
    };

    const cancelOrderWithConfirm = ({
        orderId,
        afterConfirm,
        afterSuccess,
        afterFailure,
        afterComplete,
    }: {
        orderId?: number | null;
        afterConfirm?: () => void;
    } & MutationCallbacks<CashBoxOrderDetails>) => {
        if (!orderId || !canCancelOrderPermission) return;

        ConfirmDialog.fire({
            title: getTranslatedText(t("areYouWantCancelOrder")),
            showDenyButton: false,
            showCancelButton: true,
            cancelButtonText: getTranslatedText(t("no")),
            confirmButtonText: getTranslatedText(t("yes")),
            focusCancel: true,
            customClass: {
                container: '',
                popup: 'confirm-box delete-confirm-box pt-2',
                title: 'confirmation-title pb-2 fw-medium',
                confirmButton: 'action-btn confirm-btn mx-2',
                denyButton: 'action-btn deny-btn mx-2',
                cancelButton: 'action-btn cancel-btn mx-2',
            },
        }).then(async (confirmResult) => {
            if (confirmResult.isDenied || confirmResult.isDismissed) {
                // Swal.fire('The order was not deleted', '', 'info');
                // Swal.fire(t("orderWasNotDeleted"), '', 'info');
            }
            else if (confirmResult.isConfirmed) {
                afterConfirm?.();

                const {
                    respData,
                    respError
                } = (await cancelOrder({
                    orderId,
                    afterSuccess,
                    afterFailure,
                })) || {};

                afterComplete?.({
                    respData,
                    respError
                });
            }
        });
    };

    const acceptDeliveryOrPickupOrder = async ({
        orderId,
        pickupDateTime,
        isPickup,   // External orders are 'Pickup' or 'Delivery'
        email,
        countrySymbol,
        phoneNumber,
        deliveryData,
        afterSuccess,
        afterFailure,
    }: AcceptDeliveryOrPickupOrderParams) => {
        if (!orderId) return;

        const requestData: AcceptDeliveryOrPickupOrderRequestData = {
            order_id: orderId,
            ...(pickupDateTime ? { pickup_time: pickupDateTime } : {}),
        };

        email = `${email || ""}`.trim();
        const receiverDialCode = normalizeDialCodeSymbol(countrySymbol || "at", "at");
        const phoneNumberData = {
            number: !phoneNumber
                ? ""
                : normalizePhoneLocalNumber(
                    phoneNumber,
                    receiverDialCode
                ),
            country_symbol: receiverDialCode,
        };

        if (isPickup) {
            if (email) {
                requestData.email = email;
            }
            if (phoneNumberData.number) {
                requestData.phone_number = phoneNumberData;
            }
        } else {
            requestData.delivery = {
                latitude: deliveryData?.receiverLatitude ?? null,
                longitude: deliveryData?.receiverLongitude ?? null,
                full_address: deliveryData?.fullAddress ?? "",
                country: deliveryData?.country ?? null,
                state: deliveryData?.state ?? null,
                city: deliveryData?.city ?? null,
                locality: deliveryData?.locality ?? null,
                street: deliveryData?.street ?? null,
                house_number: deliveryData?.houseNumber ?? null,
                // postal_code: deliveryData?.postalCode ?? null,
                postal: deliveryData?.postalCode ?? null,
                // apartment_door:  deliveryData?.doorNumber ?? null,
                door_number: deliveryData?.doorNumber ?? null,
                apartment_entrance: deliveryData?.apartmentEntrance ?? null,
                floor: deliveryData?.floor ?? null,
                // address_extra_details: deliveryData?.extraDetails ?? null,
                extra_details: deliveryData?.extraDetails ?? null,
                description: deliveryData?.description ?? null,
                full_name: deliveryData?.receiverFullName ?? null,
                email: email ?? null,
                phone_number: phoneNumberData.number ? phoneNumberData : null,
            };
        }

        const acceptEndpoint = isPickup ? "/order/accept_pickup" : "/order/accept_delivery";

        const {
            data: respData,
            error: respError,
        } = await sendAxiosRequestWithRetry<CashBoxOrderDetails>({
            method: "post",
            url: acceptEndpoint,
            data: requestData,
        });

        if (respData) {
            if (currentOrderIdRef.current === respData.id) {
                dispatch(updateTableOrder({
                    orderDetails: respData
                }));
            }
            if (
                (currentTableIdRef.current && currentTableIdRef.current === respData.table_id) ||
                (currentOrderTypeRef.current && currentOrderTypeRef.current === respData.order_type)
            ) {
                updateSelectedTableOrders({
                    tableId: respData.table_id,
                    orderType: respData.order_type,
                });
            }

            afterSuccess?.(respData);
        }
        else if (respError) {
            showServerErrorToast({
                err_status: respError.statusCode,
                err_message: respError?.message || "",
                list_errors: respError.details?.errors,
                opts: {
                    errorTitle: t('Error'),
                },
            });

            afterFailure?.(respError);
        }

        return {
            respData,
            respError
        }
    };


    /* ---------------------------
    * Items
    * --------------------------- */
    const updateEdibleCategoriesList = async(silentFetch?: boolean) => {
        if (!availableEdiblesListRef.current || !silentFetch) {
            dispatch(setEdibleCategoriesLoading(true));
        }

        const {
            data: respData,
            error: respError
        } = await sendAxiosRequestWithRetry<CashBoxEdibleDetailedCategory[]>({
            method: "get",
            url: "/category",
        });

        if (respData) {
            dispatch(setEdibleCategories({
                edibleCategories: respData,
            }));
        }
        else if (respError) {
            const errorText = getApiErrorText(respError);

            showErrorToast({
                message: errorText,
                status: respError.statusCode,
            });
        }

        dispatch(setEdibleCategoriesLoading(false));
    };

    const goToAvailableItemsStep = async ({
        catIdOrType,
        orderType,
        noNeedToUpdate,
        categoriesSilentFetch = true,
        doNotUpdateCategories,
    }: {
        catIdOrType?: number | string | null;
        orderType?: ApiOrderType | null;
        noNeedToUpdate?: boolean;
        categoriesSilentFetch?: boolean;
        doNotUpdateCategories?: boolean;
    }) => {
        dispatch(selectStep({
            targetStepId: 'availableItemsShowStep',
        }));

        if (noNeedToUpdate) return;

        if (!doNotUpdateCategories) {
            updateEdibleCategoriesList(categoriesSilentFetch);
        }

        // dispatch(setAvalableItems({
        //     availableItemsList: null,
        // }));

        dispatch(activateLoaderForAvailableItemsList());

        dispatch(toggleSwapToOrderAddedItemsInMobileView({
            swapToOrderAddedItemsBox: false
        }));

        const url = !catIdOrType || catIdOrType !== "custom_item" ? "food/list" : "custom_food/list";

        const {
            data: availableItems_respData,
            // error: availableItems_respError
        } = await sendAxiosRequestWithRetry<CashBoxNormalFoodData[] | CashBoxCustomFoodData[]>({
            method: "get",
            url,
            params: {
                // cat_id: catIdOrType !== "custom_item" ? catIdOrType : "subway",
                cat_id: catIdOrType,
                order_type: orderType || null
            },
        });

        if (availableItems_respData) {
            dispatch(setAvalableItems({
                availableItemsList: availableItems_respData,
            }));
        }
        // else if (availableItems_respError) {
        //     //
        // };
    };

    const addItemToOrder = async ({
        orderId,
        itemType = "normal",
        itemData,
        afterComplete,
    }: AddEdibleItemParams) => {
        const targetOrderId = orderId || currentOrderIdRef.current;

        if (!targetOrderId || typeof itemData != "object") return;

        dispatch(setOrderAddedItemsLoading(true));

        if (itemType === "normal" || itemType === "custom") {
            if (!isMobileViewRef.current) {
                goToAvailableItemsStep({
                    noNeedToUpdate: true
                });
            } else {
                goToOrdersListStep({
                    noNeedToUpdate: true,  // It will be update after sending request to add an item to the order
                });
            }
        }

        dispatch(toggleSwapToOrderAddedItemsInMobileView({
            swapToOrderAddedItemsBox: true
        }));

        const itemDataKey =
            itemType === "custom" ? "custom_items" :
            itemType === "manual" ? "free_items" :
            "items";

        const {
            data: respData,
            error: respError
        } = await sendAxiosRequestWithRetry<CashBoxOrderDetails>({
            method: "post",
            url: "/order/add_items_to_order",
            data: {
                order_id: targetOrderId,
                [itemDataKey]: [itemData]
            }
        });

        if (respData) {
            const updatedOrdersList = getOrdersListWithUpdatedCurrentOrder(respData);

            dispatch(setTableOrdersListData({
                tableOrdersList: updatedOrdersList,
            }));

            dispatch(changeCurrentOrderData({
                currentOrderDetails: respData,
            }));
        }
        // else if (respError) {
        //     //
        // }

        afterComplete?.({
            respData,
            respError
        });

        dispatch(setOrderAddedItemsLoading(false));
    };

    const updateItemOfOrder = async ({
        orderId,
        itemType = "normal",
        itemData,
        dontShowLoadingForAllItems,
        afterComplete,
    }: UpdateEdibleItemParams) => {
        const targetOrderId = orderId || currentOrderIdRef.current;

        if (
            !targetOrderId ||
            !canEditTargetOrderItem({
                orderId: targetOrderId,
                itemId: itemData.item_id,
                itemType,
            })
        ) {
            if (!targetOrderId) {
                showCustomErrorToast({
                    title: t("itemEditFailed"),
                    message: t("problemInReadingItemEssentialDataMessage")
                });
            } else {
                showCustomErrorToast({
                    title: t("itemEditFailed"),
                    message: t("thisItemCannotBeEdited")
                });
            }
            return "required_data_unavailable";
        }

        const new_data = {
            order_id: targetOrderId,
            item_id: itemData.item_id,
            count: itemData.count,
            ...(itemType === "normal" && {
                ...((itemData as UpdateNormalEdibleItemData).option_id &&
                    { option_id: (itemData as UpdateNormalEdibleItemData).option_id }
                ),
                additive_ids: Array.isArray((itemData as UpdateNormalEdibleItemData).additive_ids)
                    ? (itemData as UpdateNormalEdibleItemData).additive_ids
                    : [],
            }),
            ...(itemType === "custom" && {
                options: (itemData as UpdateCustomEdibleItemData).option_ids,
            }),
            ...(itemType === "manual" && {
                price: (itemData as UpdateManualEdibleItemData).price,
                vat: (itemData as UpdateManualEdibleItemData).vat,
            }),
        };

        if (!dontShowLoadingForAllItems) {
            dispatch(setOrderAddedItemsLoading(true));
        }

        const {
            data: respData,
            error: respError
        } = await sendAxiosRequestWithRetry<CashBoxOrderDetails>({
            method: "put",
            url:
                itemType === "custom" ? "/order/edit_item_of_custom_order" :
                itemType === "manual" ? "/order/edit_free_item_of_order" :
                "/order/edit_item_of_order",
            data: new_data
        });

        if (respData) {
            const newOrdersListData = currentTableOrdersListRef.current?.map(order => {
                const order_clone = {...order};
                if (order_clone.id === targetOrderId) {
                    order_clone.cost = respData.cost;
                    order_clone.order_status_key = respData.order_status_key;
                    order_clone.order_status_title = respData.order_status_title;
                }
                return order_clone;
            });

            dispatch(setTableOrdersListData({
                tableOrdersList: newOrdersListData,
            }));

            dispatch(changeCurrentOrderData({
                currentOrderDetails: respData,
            }));
        }
        else if (respError) {
            const errorText = getApiErrorText(respError);

            showErrorToast({
                message: errorText,
                status: respError.statusCode,
            });
        }

        afterComplete?.({
            respData,
            respError
        });

        if (!dontShowLoadingForAllItems) {
            dispatch(setOrderAddedItemsLoading(false));
        }

        return !!respData ? "succeeded" : "failed";
    };

    const deleteItemOfOrder = ({
        orderId,
        id: itemId,
        itemType = "normal",
    }: {
        orderId?: number | null;
        id?: CashBoxId | null;
        itemType?: EdibleGeneralType;
    }) => {
        if (!itemId || !canEditTargetOrderItem({ orderId, itemId, itemType })) {
            if (!itemId) {
                showCustomErrorToast({
                    title: t("itemEditFailed"),
                    message: t("problemInReadingItemEssentialDataMessage")
                });
            } else {
                showCustomErrorToast({
                    title: t("itemEditFailed"),
                    message: t("thisItemCannotBeEdited")
                });
            }
            return;
        }

        const targetOrderId = orderId || currentOrderIdRef.current;

        ConfirmDialog.fire({
            title: getTranslatedText(t("areYouWantDeleteThisItem")),
            // showDenyButton: true,
            // showCancelButton: false,
            // denyButtonText: `No`,
            showDenyButton: false,
            showCancelButton: true,
            confirmButtonText: getTranslatedText(t("yesDeleteIt")),
            cancelButtonText: getTranslatedText(t("no")),
            focusCancel: true,
            customClass: {
                container: '',
                popup: 'confirm-box delete-confirm-box pt-2',
                title: 'confirmation-title pb-2 fw-medium',
                confirmButton: 'action-btn confirm-btn mx-2',
                denyButton: 'action-btn deny-btn mx-2',
                cancelButton: 'action-btn cancel-btn mx-2',
            },
        }).then(async (confirmResult) => {
            if (confirmResult.isDenied || confirmResult.isDismissed) {
                // Swal.fire('The order was not deleted', '', 'info');
                // Swal.fire(t("orderWasNotDeleted"), '', 'info');
            }
            else if (confirmResult.isConfirmed) {
                dispatch(setOrderAddedItemsLoading(true));

                const sub_url =
                    itemType === "custom"
                        ? "order/delete_custom_order" :
                    itemType === "manual"
                        ? "order/delete_free_item_from_order"
                        : "order/delete_item_from_order";

                const {
                    data: respData,
                    // error: respError
                } = await sendAxiosRequestWithRetry<CashBoxOrderDetails>({
                    method: "delete",
                    url: `/${sub_url}`,
                    data: {
                        order_id: targetOrderId,
                        item_id: itemId
                    }
                });

                if (respData) {
                    const newOrdersListData = currentTableOrdersListRef.current?.map(order => {
                        const order_clone = {...order};
                        if (order_clone.id === targetOrderId) {
                            order_clone.cost = respData.cost;
                            order_clone.order_status_key = respData.order_status_key;
                            order_clone.order_status_title = respData.order_status_title;
                        }
                        return order_clone;
                    });

                    dispatch(setTableOrdersListData({
                        tableOrdersList: newOrdersListData,
                    }));
                    dispatch(changeCurrentOrderData({
                        currentOrderDetails: respData,
                    }));
                }
                // else {
                //     //
                // }

                dispatch(setOrderAddedItemsLoading(false));
            }
        });
    };

    const getNormalOrCustomEdibleInfo = async ({
        itemId,
        itemType,
    }: {
        itemId,
        itemType: Exclude<EdibleGeneralType, "manual">
    }) => {
        const apiResult = await sendAxiosRequestWithRetry<CashBoxNormalFoodDetailedData | CashBoxCustomItemStep[]>({
            method: "get",
            url: itemType === "custom"
                ? "custom_food/details"
                : "food/details",
            params: {
                ...(itemType === "custom" && { group_code: itemId }),
                ...(itemType === "normal" && { food_id: itemId }),
            },
        });

        return apiResult;
    };

    const provideEditingItemModalData = async ({
        id,
        itemId,
        itemType,
        currentItemData,
    }: EditingItemParams) => {
        if (
            !id ||
            !itemId ||
            !canEditTargetOrderItem({ itemId: id, itemType })
        ) {
            if (!id || !itemId) {
                showCustomErrorToast({
                    title: t("itemEditFailed"),
                    message: t("problemInReadingItemEssentialDataMessage")
                });
            } else {
                showCustomErrorToast({
                    title: t("itemEditFailed"),
                    message: t("thisItemCannotBeEdited")
                });
            }
            return;
        }

        dispatch(changeCurrentEditingData({
            edibleTypeForPreviewOrEdit: itemType,
            initialItemCustomizationForEditing: null,
        }));

        dispatch(setItemPreviewBoxLoading(true));

        const {
            data: respData,
            error: respError
        } = await getNormalOrCustomEdibleInfo({
            itemId,
            itemType
        });

        if (respData) {
            dispatch(setEdibleItemPreviewDetails({
                previewItemDetails: itemType === "normal" ? (respData as CashBoxNormalFoodDetailedData) : null,
                previewCustomItemStepsData: itemType === "custom" ? (respData as CashBoxCustomItemStep[]) : null,
            }));
            dispatch(changeCurrentEditingData({
                edibleTypeForPreviewOrEdit: itemType,
                initialItemCustomizationForEditing: currentItemData,
            }));
        }
        else if (respError) {
            const errorText = getApiErrorText(respError);

            showErrorToast({
                message: errorText,
                status: respError.statusCode,
            });
        }

        dispatch(setItemPreviewBoxLoading(false));
    };

    const clearEditingItemModalData = () => {
        dispatch(setEdibleItemPreviewDetails({
            previewItemDetails: null,
            previewCustomItemStepsData: null,
        }));
        dispatch(changeCurrentEditingData({
            edibleTypeForPreviewOrEdit: null,
            initialItemCustomizationForEditing: null,
        }));
    };


    /* ---------------------------
    * Item Preview
    * --------------------------- */
    const goToItemPreview = async ({
        itemId,
        itemType
    }: ItemPreviewParams) => {
        if (!itemId || itemType === "manual") return;

        dispatch(setItemPreviewBoxLoading(true));

        dispatch(selectStep({
            targetStepId: 'itemDetailsPreviewStep',
        }));

        // dispatch(setEdibleItemPreviewDetails({
        //     previewItemDetails: null,
        //     previewCustomItemStepsData: null,
        // }));

        dispatch(changeCurrentEditingData({
            edibleTypeForPreviewOrEdit: itemType,
        }));

        const {
            data: respData,
            error: respError
        } = await getNormalOrCustomEdibleInfo({
            itemId,
            itemType
        });

        if (respData) {
            dispatch(setEdibleItemPreviewDetails({
                previewItemDetails: itemType === "normal" ? (respData as CashBoxNormalFoodDetailedData) : null,
                previewCustomItemStepsData: itemType === "custom" ? (respData as CashBoxCustomItemStep[]) : null,
            }));
        }
        else if (respError) {
            //
        }

        dispatch(setItemPreviewBoxLoading(false));
    };

    const getPreviewDataOfSelectedCustomItemOptions = async ({
        customizationData,
    } : {
        customizationData: AddCustomEdibleItemData | UpdateCustomEdibleItemData,
    }) => {
        const apiResult = await sendAxiosRequestWithRetry<CashBoxApiDataFor<"/order/custom_order_preview">>({
            method: "post",
            url: "/order/custom_order_preview",
            data: {
                items: customizationData.option_ids,
            },
        });

        return apiResult;
    };


    /* ---------------------------
    * Send to kitchen
    * --------------------------- */
    const sendSelectedItemsTokitchen = async ({
        orderId,
        description,
        selectedItems,
        selectedCustomItems,
        selectedManualItems,
    }: SendToKitchenParams) => {
        const data = {
            order_id: orderId,
            description,
            selected_items: selectedItems,
            selected_custom_items: selectedCustomItems,
            selected_free_items: selectedManualItems
        };

        dispatch(setOrderAddedItemsLoading(true));

        const {
            data: respData,
            // error: respError
        } = await sendAxiosRequestWithRetry<CashBoxOrderDetails>({
            method: "post",
            url: "/order/send_to_kitchen",
            data
        });

        if (respData) {
            refetchOrderDetailsAndUpdate({
                orderId: currentOrderIdRef.current
            });

            updateOrdersList({
                tableId: currentTableIdRef.current,
                orderType: currentOrderTypeRef.current,
            });
        }
    };

    const deliverOrder = ({
        orderId,
    }: { orderId?: number }) => {
        ConfirmDialog.fire({
            title: getTranslatedText(t("orderDeliveredConfirmation")),
            showDenyButton: false,
            showCancelButton: true,
            cancelButtonText: getTranslatedText(t("no")),
            confirmButtonText: getTranslatedText(t("yes")),
            focusCancel: true,
            customClass: {
                container: '',
                popup: 'confirm-box delete-confirm-box pt-2',
                title: 'confirmation-title pb-2 fw-medium',
                confirmButton: 'action-btn confirm-btn mx-2',
                denyButton: 'action-btn deny-btn mx-2',
                cancelButton: 'action-btn cancel-btn mx-2',
            },
        }).then(async (confirmResult) => {
            if (confirmResult.isDenied || confirmResult.isDismissed) {
                // Swal.fire(t(""), '', 'info');
            } else if (confirmResult.isConfirmed) {
                const {
                    data: respData,
                    // error: respError
                } = await sendAxiosRequestWithRetry<CashBoxOrderDetails>({
                    method: "post",
                    url: "/order/order_delivered",
                    data: {
                        order_id: orderId
                    }
                });
                if (respData) {
                    const updatedOrdersList = getOrdersListWithUpdatedCurrentOrder(respData);

                    dispatch(setTableOrdersListData({
                        tableOrdersList: updatedOrdersList,
                    }));

                    dispatch(changeCurrentOrderData({
                        currentOrderDetails: respData,
                    }));

                    // dispatch(setSelectedOrder({
                    //     orderId: null,
                    // }));
                }
            }
        });
    };


    /* ---------------------------
    * Change Table
    * --------------------------- */
    const changeOrderTable = async ({
        tableId,
        orderId,
    }: ChangeTableParams) => {
        goToTablesListStepAndUpdateData();

        const resp = await sendAxiosRequestWithRetry<CashBoxOrderDetails>({
            method: "post",
            url: "/order/change_table",
            data: {
                table_id: tableId,
                order_id: orderId
            }
        });

        if (resp.data) {
            // do something like display a notification in the header in order to show the 'id' of the destination table
        }
        else if (resp.error) {
            //
        }

        return resp;
    };


    /* ---------------------------
    * Demo mode
    * --------------------------- */
    const toggleDemoMode = async ({
        demoMode,
        targetTableId,
        targetOrderType,
        targetOrderId,
        afterSuccess,
        afterError,
    }: ToggleDemoModeParams) => {
        if (!canUseDemoModePermission) return;

        dispatch(activateLoaderForOrdersList());

        dispatch(setTableOrdersListData({
            tableOrdersList: [],
            tableOlderOrdersList: [],
            // orderId: null,
        }));

        dispatch(setSelectedOrder({
            orderId: null,
        }));

        dispatch(setShowDemoModeBtnLoader({
            show: true
        }));

        const {
            data: respData,
            error: respError
        } = await sendAxiosRequestWithRetry<CashBoxDemoModeResponse>({
            method: "put",
            url: "/setting/demo_mode",
            data: {
                is_demo_mode: demoMode
            }
        });

        if (respData) {
            const isDemoMode = respData?.is_demo_mode;
            dispatch(changeDemoModeState({
                isDemoMode,
            }));

            if (targetOrderId || currentOrderIdRef.current) {
                selectOrder({
                    orderId: targetOrderId || currentOrderIdRef.current,
                });
            }
            else if (targetTableId || targetOrderType || currentTableIdRef.current || currentOrderTypeRef.current) {
                updateOrdersList({
                    tableId: targetTableId || currentTableIdRef.current,
                    orderType: targetOrderType || currentOrderTypeRef.current,
                });
            }
            else {
                goToTablesListStepAndUpdateData();
            }

            afterSuccess?.(respData?.is_demo_mode);
        }
        else if (respError) {
            const errorText = getApiErrorText(respError);

            showErrorToast({
                message: errorText,
                status: respError.statusCode,
            });

            afterError?.(respError);
        }

        dispatch(setShowDemoModeBtnLoader({
            show: false
        }));
    };

    const toggleOrderDemo = ({
        orderId,
        afterSuccess,
        afterError,
    }: ToggleOrderDemoParams) => {
        if (!canUseDemoModePermission) return;

        const isConvertingOrderToLiveMode = !!appIsDemoModeRef.current;

        ConfirmDialog.fire({
            title: (
                <Trans
                    i18nKey={isConvertingOrderToLiveMode
                        ? "areYouSureYouWantToConvertOrderToLiveModeOrder"
                        : "areYouSureYouWantToConvertOrderToDemoModeOrder"
                    }
                    values={{ orderId }}
                    components={[
                        <></>,
                        <strong className="text-decoration-underline" />,
                        <></>,
                    ]}
                />
            ),
            ...(isConvertingOrderToLiveMode ? {} : {
                text: getTranslatedText(t("demoModeAlertMessage")),
            }),
            // showDenyButton: true,
            // showCancelButton: false,
            // denyButtonText: `No`,
            showDenyButton: false,
            showCancelButton: true,
            confirmButtonText: getTranslatedText(t("yes")),
            cancelButtonText: getTranslatedText(t("no")),
            focusCancel: true,
            customClass: {
                container: '',
                popup: 'confirm-box delete-confirm-box pt-2',
                title: 'confirmation-title pb-2 fw-medium',
                confirmButton: 'action-btn confirm-btn mx-2',
                denyButton: 'action-btn deny-btn mx-2',
                cancelButton: 'action-btn cancel-btn mx-2',
            },
        }).then(async (confirmResult) => {
            if (confirmResult.isDenied || confirmResult.isDismissed) {
                // Swal.fire('------', '', 'info');
            }
            else if (confirmResult.isConfirmed) {
                dispatch(setSelectedOrder({
                    orderId: null,
                }));

                dispatch(activateLoaderForOrdersList());

                const {
                    data: respData,
                    error: respError
                } = await sendAxiosRequestWithRetry<CashBoxOrderDetails>({
                    method: "post",
                    url: "/order/convert_order",
                    data: {
                        order_id: orderId
                    }
                });

                if (respData) {
                    updateOrdersList({
                        tableId: currentTableIdRef.current,
                        orderType: currentOrderTypeRef.current,
                    });

                    afterSuccess?.(respData);
                }
                else if (respError) {
                    const errorText = getApiErrorText(respError);

                    showErrorToast({
                        message: errorText,
                        status: respError.statusCode,
                    });

                    afterError?.(respError)
                }
            }
        });
    };


    const value = useMemo<OrderFlowContextValue>(() => ({
        showOlderOrders,
        showDeliveredOrders,
        showPaidOrders,
        changeFiltersAppliedToOrdersList,

        goToTargetStepFromSearchParams,

        updateTablesList,
        goToTablesListStep,
        goToTablesListStepAndUpdateData,

        selectTable,
        updateSelectedTableOrders,
        goToOrdersListStep,
        updateOrdersList,
        createOrder,
        openOrderByOrderDetails,
        refetchOrderDetailsAndUpdate,
        selectOrder,
        unselectOrder,
        cancelOrder,
        cancelOrderWithConfirm,
        acceptDeliveryOrPickupOrder,

        goToAvailableItemsStep,
        addItemToOrder,
        updateItemOfOrder,
        deleteItemOfOrder,
        provideEditingItemModalData,
        clearEditingItemModalData,

        goToItemPreview,
        getPreviewDataOfSelectedCustomItemOptions,

        sendSelectedItemsTokitchen,
        deliverOrder,

        changeOrderTable,

        toggleDemoMode,
        toggleOrderDemo,
    }), [
        showOlderOrders,
        showDeliveredOrders,
        showPaidOrders,

        searchParams,
    ]);

    return (
        <OrderFlowContext.Provider value={value}>
            {children}
        </OrderFlowContext.Provider>
    );
};

/* ---------------------------
 * Consumer hook
 * --------------------------- */
export const useOrderFlow = () => {
    const ctx = useContext(OrderFlowContext);

    if (!ctx) {
        throw new Error('useOrderFlow must be used inside OrderFlowProvider');
    }

    return ctx;
};
